/* Copyright (c) 2024 Xperi. All Rights Reserved. */

export class DateTime {
    private static TAG: string = 'DateTime';

    private static LONG_DASH: string = '\u2013';

    private mDate: Date;

    public constructor(value?: string | number | Date) {
        this.mDate = value || value === 0 ? new Date(value) : new Date();
    }

    /**
     * Adds a specified number of hours to the current DateTime object and returns a new DateTime
     * object representing the result.
     *
     * @param hours (number) - The number of hours to add.
     * @returns `DateTime`: A new DateTime object with the specified hours added.
     */
    public addHours(hours: number): DateTime {
        return this.addMinutes(hours * 60);
    }

    /**
     * Adds the specified number of milliseconds to the current DateTime object and returns a new
     * DateTime object representing the result.
     *
     * @param millis (number) - The number of milliseconds to add.
     * @returns `DateTime`: A new DateTime object with the specified milliseconds added.
     */
    public addMilliseconds(millis: number): DateTime {
        return new DateTime(this.getTime() + millis);
    }

    /**
     * Adds the specified number of minutes to the current DateTime object and returns a new
     * DateTime object representing the result.
     *
     * @param minutes (number) - The number of minutes to add.
     * @returns `DateTime`: A new DateTime object with the specified minutes added.
     */
    public addMinutes(minutes: number): DateTime {
        return this.addSeconds(minutes * 60);
    }

    /**
     * Adds the specified number of seconds to the current DateTime object and returns a new
     * DateTime object representing the result.
     *
     * @param seconds (number) - The number of seconds to add.
     * @returns `DateTime`: A new DateTime object with the specified seconds added.
     */
    public addSeconds(seconds: number): DateTime {
        return this.addMilliseconds(seconds * 1000);
    }

    /**
     * Rounds`up` the current DateTime object to the nearest multiple of the specified number of
     * minutes and returns a new DateTime object.
     *
     * @param minutes (number) - The number of minutes to round up to.
     * @returns `DateTime`: A DateTime object rounded down to the specified number of minutes.
     */
    public ceilByMinutes(minutes: number): DateTime {
        const ceilTime = new DateTime(this.getTime() + (minutes / 2) * 60 * 1000);
        return ceilTime.roundByMinutes(minutes);
    }

    /**
     * Rounds `down` the current DateTime object to the nearest multiple of the specified number of
     * minutes and returns a new DateTime object.
     *
     * @param minutes (number) - The number of minutes to round down to.
     * @returns `DateTime`: A DateTime object rounded down to the specified number of minutes.
     */
    public floorByMinutes(minutes: number): DateTime {
        const floorTime = new DateTime(this.getTime() - (minutes / 2) * 60 * 1000);
        return floorTime.roundByMinutes(minutes);
    }

    /**
     * This function generates time intervals within a specified duration, with each interval's
     * length determined by the `span` parameter. It takes two optional parameters: `span` and
     * `duration`.
     *
     * @param span determines the length of each interval in milliseconds (default is 30 minutes).
     * @param duration specifies how many milliseconds the intervals should span
     * (default is 2 hours).
     * @returns `sting[]`: Returns the array containing the generated intervals.
     */
    public generateIntervals(span = 30 * 60 * 1000, duration = 2 * 60 * 60 * 1000): string[] {
        const intervals: string[] = [];
        for (let i = 0; i < duration / span; i += 1) {
            const millis = i * span;
            intervals.push(this.addMilliseconds(millis).to12HourClock());
        }
        return intervals;
    }

    /**
     * Returns the time in milliseconds since the Unix epoch.
     *
     * @returns `number`: The time in milliseconds.
     */
    public getTime(): number {
        return this.mDate.getTime();
    }

    /**
     * Compares today's date with the current DateTime object to determine whether the DateTime
     * is today.
     *
     * @returns boolean
     */
    public isToday(): boolean {
        const today = new Date();
        return (
            this.mDate.getFullYear() === today.getFullYear() &&
            this.mDate.getMonth() === today.getMonth() &&
            this.mDate.getDate() === today.getDate()
        );
    }

    /**
     * Rounds the current DateTime object to the nearest multiple of the specified number of
     * minutes and returns a new DateTime object.
     * @param minutes (number) - The number of minutes to round to.
     * @returns `DateTime`: A new DateTime rounded to the nearest multiple of the specified minutes.
     */
    public roundByMinutes(minutes: number): DateTime {
        const retVal = new Date(this.getTime());
        retVal.setMilliseconds(Math.round(retVal.getMilliseconds() / 1000) * 1000);
        retVal.setSeconds(Math.round(retVal.getSeconds() / 60) * 60);
        retVal.setMinutes(Math.round(retVal.getMinutes() / minutes) * minutes);
        return new DateTime(retVal.getTime());
    }

    /**
     * Formats a date according to the specified format string.
     *
     * @param format (string): The format string specifying how the date should be formatted.
     * @param lang (string):The language code specifying the desired language for the output.
     *             Example: 'en' for English, 'fr' for French, etc.
     * @returns `string`: The formatted date string.
     */
    public strftime(format: string, lang?: string): string {
        const formatOptions = {
            '%y': this.mDate.getFullYear().toString().slice(-2),
            '%Y': this.mDate.getFullYear(),
            '%m': (this.mDate.getMonth() + 1).toString().padStart(2, '0'),
            '%M': (this.mDate.getMonth() + 1).toString(),
            '%d': this.mDate.getDate().toString().padStart(2, '0'),
            '%D': this.mDate.getDate().toString(),
            '%h': this.mDate.getHours().toString().padStart(2, '0'),
            '%H': this.mDate.getHours().toString(),
            '%l': (this.mDate.getHours() % 12 || 12).toString(),
            '%L': (this.mDate.getHours() % 12 || 12).toString().padStart(2, '0'),
            '%i': this.mDate.getMinutes().toString().padStart(2, '0'),
            '%I': this.mDate.getMinutes().toString(),
            '%s': this.mDate.getSeconds().toString().padStart(2, '0'),
            '%S': this.mDate.getSeconds().toString(),
            '%a': this.mDate.toLocaleDateString(lang, { weekday: 'short' }),
            '%A': this.mDate.toLocaleDateString(lang, { weekday: 'long' }),
            '%p': this.mDate.getHours() >= 12 ? 'pm' : 'am',
            '%P': this.mDate.getHours() >= 12 ? 'PM' : 'AM',
        };

        const formattedDate = format.replace(/%[yYmMdDhHliIsSaAp]/g, (match) => {
            return formatOptions[match] || match;
        });

        return formattedDate;
    }

    /**
     * Subtracts the specified number of hours from the current DateTime object.
     *
     * @param hours (number): The number of hours to subtract.
     * @returns `DateTime`: A new DateTime object after subtracting the specified hours.
     */
    public subtractHours(hours: number): DateTime {
        return this.subtractMinutes(hours * 60);
    }

    /**
     * Subtracts the specified number of milliseconds from the current DateTime object.
     *
     * @param millis (number): The number of milliseconds to subtract.
     * @returns `DateTime`: A new DateTime object after subtracting the specified milliseconds.
     */
    public subtractMilliseconds(millis: number): DateTime {
        return new DateTime(this.getTime() - millis);
    }

    /**
     * Subtracts the specified number of minutes from the current DateTime object.
     *
     * @param minutes (number): The number of minutes to subtract.
     * @returns `DateTime`: A new DateTime object after subtracting the specified minutes.
     */
    public subtractMinutes(minutes: number): DateTime {
        return this.subtractSeconds(minutes * 60);
    }

    /**
     * Subtracts the specified number of seconds from the current DateTime object.
     *
     * @param seconds (number): The number of seconds to subtract.
     * @returns `DateTime`: A new DateTime object after subtracting the specified seconds.
     */
    public subtractSeconds(seconds: number): DateTime {
        return this.subtractMilliseconds(seconds * 1000);
    }

    /**
     * Converts the current date to a string representation of the day and date in the
     * specified language.
     *
     * @param lang (string):The language code specifying the desired language for the output.
     *             Example: 'en' for English, 'fr' for French, etc.
     * @returns `string`: A string representing the day and date in the specified language.
     *
     * **Example**: `Wed 4/17`
     */
    public toLocaleDayWithDate(lang: string): string {
        const formattedDate = this.mDate.toLocaleDateString(lang, {
            weekday: 'short',
        });
        const day = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
        const date = this.mDate.getDate();
        const month = this.mDate.getMonth() + 1;
        return `${day} ${month}/${date}`;
    }

    /**
     * Formats the time represented by the DateTime object in 12-hour clock format.
     *
     * @returns `string`: A string representing the time in 12-hour clock format.
     *
     * **Example**: `9:03am`, `11:27pm`
     */
    public to12HourClock(): string {
        const minutes = this.mDate.getMinutes().toString().padStart(2, '0');
        const hours24 = this.mDate.getHours();
        const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
        const period = hours24 >= 12 ? 'pm' : 'am';

        return `${hours12}:${minutes}${period}`;
    }

    /**
     * Converts the DateTime object to a string in the ISO 8601 format.
     *
     * @returns `string`: A string representing the DateTime object in the ISO 8601 format.
     */
    public toISOString(): string {
        return this.mDate.toISOString().split('.')[0].concat('Z');
    }

    /**
     * Converts a time duration in milliseconds to a human-readable format.
     *
     * @param millis (number): The time duration in milliseconds to be converted.
     * @returns `string`: A human-readable string representing the converted time duration.
     *
     * **Example**:
     *
     * ```
     * const readableTime = DateTime.convertToReadable(3600000);
     * // Returns a human-readable string representing 1 hr
     * ```
     */
    public static convertToReadable(millis: number): string {
        const minutesTotal = millis / (60 * 1000);
        const hours = Math.floor(minutesTotal / 60);
        const minutes = Math.floor(minutesTotal % 60);
        if (hours > 0) {
            if (minutes > 0) return `${hours} hr ${minutes} min`;
            return `${hours} hr`;
        }
        return `${minutes} min`;
    }

    /**
     * Creates a formatted `day date time` range string based on provided parameters such as
     * start time, end time, and duration.
     *
     * @param options (object): An object containing the following optional properties:
     *  * `startTime`: The start time of the time range. It can be provided as a string, number,
     *  or JavaScript Date object.
     *  * `endTime`: The end time of the time range. It can be provided as a string, number, or
     *  JavaScript Date object.
     *  * `duration`: The duration of the time range in milliseconds. This property is optional
     *  and will be ignored if endTime is provided.
     * @returns `string`: A formatted time range string.
     *
     * **Example**:
     *
     * ```
     * const startTime = new Date('2024-04-17T08:00:00');
     * const endTime = new Date('2024-04-17T10:00:00');
     *
     * const timeRange = createTimeRange(startTime, endTime);
     * // Returns a formatted time range string like "Mon 4/17 8:00am – 10:00am"
     * ```
     */
    public static createDayDateTimeRange(options: {
        startTime?: string | number | Date;
        endTime?: string | number | Date;
        duration?: number;
        lang?: string;
    }): string {
        const { startTime, endTime, duration, lang = 'en' } = options;
        const st: DateTime = new DateTime(startTime);
        const et: DateTime = new DateTime(endTime);
        let sTimeRange: string = '';

        if (endTime && duration) {
            const relativeStartTime = et.subtractSeconds(duration);
            sTimeRange = `${relativeStartTime.toLocaleDayWithDate(
                lang,
            )} ${relativeStartTime.to12HourClock()} ${DateTime.LONG_DASH} ${et.to12HourClock()}`;
        }

        if (startTime && duration) {
            const relativeEndTime = st.addSeconds(duration);
            sTimeRange = `${st.toLocaleDayWithDate(lang)} ${st.to12HourClock()} ${
                DateTime.LONG_DASH
            } ${relativeEndTime.to12HourClock()}`;
        }

        if (startTime && endTime) {
            sTimeRange = `${st.toLocaleDayWithDate(lang)} ${st.to12HourClock()} ${
                DateTime.LONG_DASH
            } ${et.to12HourClock()}`;
        }

        return sTimeRange;
    }

    /**
     * Creates a formatted time range string based on provided parameters such as start time,
     * end time, and duration.
     *
     * @param options (object): An object containing the following optional properties:
     *  * `startTime`: The start time of the time range. It can be provided as a string, number,
     *  or JavaScript Date object.
     *  * `endTime`: The end time of the time range. It can be provided as a string, number, or
     *  JavaScript Date object.
     *  * `duration`: The duration of the time range in milliseconds. This property is optional
     * and will be ignored if endTime is provided.
     *
     * @returns `string`: A formatted time range string.
     *
     * **Example**:
     *
     * ```
     * const startTime = new Date('2024-04-17T08:00:00');
     * const endTime = new Date('2024-04-17T10:00:00');
     *
     * const timeRange = createTimeRange(startTime, endTime);
     * // Returns a formatted time range string like "8:00am - 10:00am"
     * ```
     */
    public static createTimeRange(options: {
        startTime?: string | number | Date;
        endTime?: string | number | Date;
        duration?: number;
    }): string {
        const { startTime, endTime, duration } = options;
        const st: DateTime = new DateTime(startTime);
        const et: DateTime = new DateTime(endTime);
        let sTimeRange: string = '';

        if (endTime && duration) {
            const relativeStartTime = et.subtractSeconds(duration);
            sTimeRange = `${relativeStartTime.to12HourClock()} - ${et.to12HourClock()}`;
        }

        if (startTime && duration) {
            const relativeEndTime = st.addSeconds(duration);
            sTimeRange = `${st.to12HourClock()} - ${relativeEndTime.to12HourClock()}`;
        }

        if (startTime && endTime) {
            sTimeRange = `${st.to12HourClock()} - ${et.to12HourClock()}`;
        }

        return sTimeRange;
    }

    public getFullYearFromShortYear(shortYear: number): number {
        const currentYear = this.mDate.getFullYear();
        const currentCentury = Math.floor(currentYear / 100);
        const previousCentury = currentCentury - 1;
        const fullYear =
            shortYear <= currentYear % 100
                ? currentCentury * 100 + shortYear
                : previousCentury * 100 + shortYear;
        return fullYear;
    }

    /**
     * Returns the current time in milliseconds since the Unix epoch.
     *
     * @returns `number`: The current time in milliseconds.
     */
    public static now(): number {
        return Date.now();
    }

    /**
     * Converts seconds to a human-readable string format.
     * @param seconds the number of seconds
     * @returns a formatted time string (3 d 5 h 10 m 3 s)
     */
    public static secondsToString(seconds) {
        const numyears = Math.floor(seconds / 31536000);
        const numdays = Math.floor((seconds % 31536000) / 86400);
        const numhours = Math.floor(((seconds % 31536000) % 86400) / 3600);
        const numminutes = Math.floor((((seconds % 31536000) % 86400) % 3600) / 60);
        const numseconds = (((seconds % 31536000) % 86400) % 3600) % 60;
        // Combination with years
        if (numyears && numseconds) {
            return `${numyears} y ${numdays} d ${numhours} h ${numminutes} m ${numseconds} s`;
        }
        if (numyears && numminutes) {
            return `${numyears} y ${numdays} d ${numhours} h ${numminutes} m`;
        }
        if (numyears && numhours) {
            return `${numyears} y ${numdays} d ${numhours} h`;
        }
        if (numyears && numdays) {
            return `${numyears} y ${numdays} d`;
        }
        if (numyears) {
            return `${numyears} y`;
        }
        // Combination with days
        if (numdays && numseconds) {
            return `${numdays} d ${numhours} h ${numminutes} m ${numseconds} s`;
        }
        if (numdays && numminutes) {
            return `${numdays} d ${numhours} h ${numminutes} m`;
        }
        if (numdays && numhours) {
            return `${numdays} d ${numhours} h`;
        }
        if (numdays) {
            return `${numdays} d`;
        }
        // Combination with hours
        if (numhours && numseconds) {
            return `${numhours} h ${numminutes} m ${numseconds} s`;
        }
        if (numhours && numminutes) {
            return `${numhours} h ${numminutes} m`;
        }
        if (numhours) {
            return `${numhours} h`;
        }

        // Combination with hours
        if (numminutes && numseconds) {
            return `${numminutes} m ${numseconds} s`;
        }
        if (numminutes) {
            return `${numminutes} m`;
        }

        return `${numseconds} s`;
    }
}
